import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Descriptions,
  Typography,
  Table,
  List,
  Image,
  Card,
  Space,
  Divider,
  Radio,
  Select,
  Spin,
  Empty
} from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import styles from './index.scss';
import { getFurtherInformation } from '../../../../../services/ns/runInstance';

import { isPlainObject } from '../../../../../util/lodashlike';
const { Text, Paragraph } = Typography;
const urlRegex = /https?:\/\/[^\s]+/;
const urlSplitRegex = /(https?:\/\/[^\s]+)/g;

const renderValue = (value) => {
  if (!urlRegex.test(value)) {
    return value;
  }

  const parts = value.split(urlSplitRegex).filter((part) => part.length > 0);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          href={part.replace(/[;,.\)]*$/, '')} //Trims unwanted chars
          target="_blank"
          rel="noopener noreferrer"
          key={index}>
          {part}
        </a>
      );
    }
    return part;
  });
};

export const renderTwoColumnKV = (data, className) => (
  <Descriptions column={2} size="small" bordered className={className}>
    {data &&
      Object.entries(data).map(([key, value]) => (
        <Descriptions.Item key={key} label={`${key}:`}>
          {renderValue(value?.toString() || '')}
        </Descriptions.Item>
      ))}
  </Descriptions>
);

const renderThreeColumnKV = (data) => (
  <Descriptions
    column={3}
    size="small"
    bordered
    className="furtherInfoDescriptions">
    {data &&
      Object.entries(data).map(([key, value]) => (
        <Descriptions.Item key={key} label={`${key}:`}>
          {renderValue(value?.toString() || '')}
        </Descriptions.Item>
      ))}
  </Descriptions>
);

const renderSingleColumnKVWithImage = (data) => (
  <div className="singleColumnContainer">
    <Descriptions
      column={1}
      colon={false}
      size="small"
      layout="vertical"
      className="furtherInfoDescriptionsFlex">
      {data?.fields &&
        Object.entries(data.fields).map(([key, value]) => (
          <Descriptions.Item key={key} label={key}>
            {renderValue(value?.toString() || '')}
          </Descriptions.Item>
        ))}
    </Descriptions>
    {data?.image?.url && (
      <div className="imageBlock">
        <Image
          src={data?.image?.url}
          width={data?.image?.width || 180}
          height={data?.image?.height || 180}
          alt="Image"
          className="furtherInfoImage"
        />
        <div className="imageCaption">
          <Text type="secondary">{data?.image?.caption}</Text>
        </div>
      </div>
    )}
  </div>
);

const renderParagraphWithImage = (data) => (
  <div className="singleColumnContainer">
    <Paragraph className="furtherInfoParagraph">{data?.text}</Paragraph>
    {data?.image?.url && (
      <div className="imageBlock">
        <Image
          src={data?.image?.url}
          width={data?.image?.width || 180}
          height={data?.image?.height || 180}
          alt="Image"
          className="furtherInfoImage"
        />
        <div className="imageCaption">
          <Text type="secondary">{data?.image?.caption}</Text>
        </div>
      </div>
    )}
  </div>
);

const renderTable = (data) => {
  const columns = data?.columns?.map((col, idx) => ({
    title: col,
    dataIndex: `col${idx}`,
    key: `col${idx}`,
    render: (text) => renderValue(text?.toString() || '')
  }));
  const dataSource = data?.rows?.map((row, idx) => {
    const obj = { key: idx };
    row.forEach((cell, i) => {
      obj[`col${i}`] = cell;
    });
    return obj;
  });

  return (
    <div>
      <Text type="secondary">{data?.description}</Text>
      <Table
        columns={columns || []}
        dataSource={dataSource || []}
        pagination={false}
        size="small"
        className="furtherInfoTable"
      />
    </div>
  );
};

const renderSingleColumnKV = (data) => (
  <Descriptions
    column={1}
    colon={false}
    size="small"
    layout="vertical"
    className="furtherInfoDescriptionsFlex">
    {data &&
      Object.entries(data).map(([key, value]) => (
        <Descriptions.Item key={key} label={key}>
          {renderValue(value?.toString() || '')}
        </Descriptions.Item>
      ))}
  </Descriptions>
);

const renderList = (data) => {
  const items = (data?.items || []).filter(
    (it) => it !== null && it !== undefined
  );
  return (
    <List
      size="small"
      bordered={false}
      dataSource={items}
      rowKey={(item, index) =>
        (typeof item === 'object' && (item.key || item.id)) ?? index
      }
      renderItem={(item) => (
        <List.Item className="furtherInfoListItem">
          <Text>{renderValue(item?.toString() || '')}</Text>
        </List.Item>
      )}
      style={{ marginTop: 8 }}
    />
  );
};

const renderMediaGallery = (data) => (
  <Image.PreviewGroup>
    <div className="furtherInfoMediaSpace">
      {data.media.map((media, idx) => (
        <div key={idx} className="furtherInfoMediaBlock">
          {media.type === 'image' ? (
            <Image
              src={media.url}
              width={110}
              height={110}
              className="furtherInfoMediaImage"
              alt="Invalid image URL"
              preview={true}
            />
          ) : (
            <div className="furtherInfoDoc">
              <FileTextOutlined />
              <a href={media.url} target="_blank" rel="noopener noreferrer">
                <div className="furtherInfoMediaMask">
                  <span className="ant-image-mask-info">View File</span>
                </div>
              </a>
            </div>
          )}
          <div className="furtherInfoMediaCaption">
            <Text ellipsis className="furtherInfoMediaCaptionText">
              {media.caption}
            </Text>
          </div>
        </div>
      ))}
    </div>
  </Image.PreviewGroup>
);

const renderEnhancedList = (
  data = [],
  selectedFilter,
  sortOrder,
  setSelectedFilter,
  setSortOrder
) => {
  // Dynamically get unique filter tags from data
  const filterOptions = Array.from(
    new Set((data || []).flatMap((item) => item.filter_tags || []))
  );

  // Calculate counts
  const tagCounts = data.reduce((acc, item) => {
    item.filter_tags.forEach((tag) => {
      acc[tag] = (acc[tag] || 0) + 1;
    });
    return acc;
  }, {});
  const allCount = data.length;

  // Filtered & sorted data
  let filteredData =
    selectedFilter === 'All'
      ? data
      : data.filter((item) => item.filter_tags.includes(selectedFilter));

  filteredData = [...filteredData].sort((a, b) => {
    if (sortOrder === 'desc') {
      return b.sort_key.localeCompare(a.sort_key);
    }
    return a.sort_key.localeCompare(b.sort_key);
  });

  // Tags
  const tags = [
    { label: `All (${allCount})`, value: 'All' },
    ...filterOptions.map((tag) => ({
      label: `${tag} (${tagCounts[tag] || 0})`,
      value: tag
    }))
  ];

  // Sort options
  const sortOptions = [
    { label: 'Newest to Oldest', value: 'desc' },
    { label: 'Oldest to Newest', value: 'asc' }
  ];

  return (
    <div className="enhancedListBlock">
      <div className="filterBlock">
        <Radio.Group
          value={selectedFilter}
          onChange={(e) => setSelectedFilter(e.target.value)}
          optionType="button"
          buttonStyle="solid">
          {tags.map((tag) => (
            <Radio.Button
              key={tag.value}
              value={tag.value}
              style={{ fontSize: 12 }}>
              {tag.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <div>
          <span style={{ marginRight: 8, color: '#888' }}>Arrange by:</span>
          <Select
            value={sortOrder}
            onChange={setSortOrder}
            options={sortOptions}
          />
        </div>
      </div>
      <div>
        {filteredData.map((item, idx) => (
          <Card key={idx} className="enhancedListCard">
            <div className="enhancedListCardHeader">
              <div className="tagContainer">
                {item.filter_tags.map((tag) => (
                  <span key={tag} className="enhancedListTag">
                    {tag}
                  </span>
                ))}
              </div>
              {item.view_pdf_url && (
                <a
                  href={item.view_pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="viewPDFButton">
                  <FileTextOutlined style={{ marginRight: 6 }} /> View PDF
                </a>
              )}
            </div>
            <div style={{ marginTop: 16 }}>
              {renderTwoColumnKV(item.key_values)}
            </div>
            {item.footer_values && item.footer_values.length > 0 && (
              <>
                <Divider />
                <div className="footerValues">
                  <div>{item.footer_values[0]}</div>
                  <div>{item.footer_values[1]}</div>
                </div>
              </>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

const blockRenderers = {
  two_column_kv: (data) => renderTwoColumnKV(data, 'furtherInfoDescriptions'),
  three_column_kv: renderThreeColumnKV,
  single_column_kv_with_image: renderSingleColumnKVWithImage,
  paragraph_with_image: renderParagraphWithImage,
  table: renderTable,
  single_column_kv: renderSingleColumnKV,
  list: renderList,
  media_gallery: renderMediaGallery,
  historical_watchlist_card: renderEnhancedList
};

const FurtherInformation = ({
  hitId,
  isFromRelatedParty,
  relatedPartyFurtherInfo
}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState('All');
  const [sortOrder, setSortOrder] = React.useState('desc');

  useEffect(() => {
    if (isFromRelatedParty) {
      if (typeof relatedPartyFurtherInfo === 'string') {
        try {
          setData(JSON.parse(relatedPartyFurtherInfo));
        } catch {
          setData(relatedPartyFurtherInfo);
        }
      }
    } else {
      setIsLoading(true);
      getFurtherInformation({ hitId })
        .then(({ furtherInformation }) => {
          if (typeof furtherInformation === 'string') {
            try {
              setData(JSON.parse(furtherInformation));
            } catch {
              setData(furtherInformation);
            }
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [hitId, isFromRelatedParty, relatedPartyFurtherInfo]);

  const renderArray = useCallback(
    (value) => {
      return value.map((item, index) => {
        return (
          <React.Fragment key={index}>
            {renderValue(item?.toString() || '')}
            {index !== value.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      });
    },
    [renderValue]
  );

  const renderBlocks = (blocks) => {
    return (
      <>
        {blocks?.length > 0 ? (
          blocks?.map((block) => (
            <Card
              key={block?.type}
              className="furtherInfoBlockCard"
              bodyStyle={undefined}>
              {block?.title && (
                <div className={styles.blockTitle}>{block.title}</div>
              )}
              {blockRenderers[block?.type] ? (
                blockRenderers[block.type](
                  block.data,
                  selectedFilter,
                  sortOrder,
                  setSelectedFilter,
                  setSortOrder
                )
              ) : (
                <Text type="danger">Unknown block type: {block.type}</Text>
              )}
            </Card>
          ))
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              !isLoading ? 'No further information available' : 'Loading...'
            }
          />
        )}
      </>
    );
  };

  const renderJsonObject = (objectData) => (
    <div>
      {Object.entries(objectData)?.map(([key, value]) => (
        <div key={key} className="jsonRenderBlock">
          <span className="jsonRenderBlockKey">{key}:</span>
          <span className="jsonRenderBlockValue">
            {(Array.isArray(value)
              ? renderArray(value)
              : renderValue(value?.toString() || '')) || '-'}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <Spin spinning={isLoading}>
      <Space direction="vertical" size={24} className={styles.furtherInfoBlock}>
        {(() => {
          if (data?.blocks) {
            return renderBlocks(data?.blocks);
          } else if (data) {
            if (isPlainObject(data)) {
              return renderJsonObject(data);
            } else {
              return <p>{data}</p>;
            }
          } else {
            return (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  !isLoading ? 'No further information available' : 'Loading...'
                }
              />
            );
          }
        })()}
      </Space>
    </Spin>
  );
};

FurtherInformation.propTypes = {
  hitId: PropTypes.string,
  isFromRelatedParty: PropTypes.bool,
  relatedPartyFurtherInfo: PropTypes.string
};

export default FurtherInformation;
